import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Calendar, ArrowRight, Loader2, Sparkles, Clock, CheckCircle2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");

const STATUS_META = {
  current: { label: "In corso", icon: Clock, color: "#4A6B53" },
  upcoming: { label: "In arrivo", icon: Sparkles, color: "#C8795A" },
  past: { label: "Realizzati", icon: CheckCircle2, color: "#7A7369" },
};

export default function Projects() {
  const [tab, setTab] = useState("current");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/projects").then((r) => setProjects(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => projects.filter((p) => p.status === tab), [projects, tab]);
  const counts = useMemo(() => ({
    current: projects.filter((p) => p.status === "current").length,
    upcoming: projects.filter((p) => p.status === "upcoming").length,
    past: projects.filter((p) => p.status === "past").length,
  }), [projects]);

  return (
    <div className="bg-[#FBF9F6] min-h-[calc(100vh-80px)]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=2000" alt="" className="w-full h-full object-cover"/>
          <div className="absolute inset-0 bg-gradient-to-br from-[#2C312E]/85 via-[#4A6B53]/60 to-[#2C312E]/70"/>
        </div>
        <div className="relative mx-auto max-w-6xl px-6 md:px-10 py-20 md:py-28">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#E4B871] mb-3">Progetti del Network</div>
          <h1 className="font-display font-black text-4xl md:text-6xl text-white leading-tight max-w-3xl">
            Cosa stiamo facendo,<br/>cosa sogniamo e cosa <span className="text-[#E4B871]">abbiamo creato</span>.
          </h1>
          <p className="mt-5 text-white/85 max-w-2xl text-lg">
            Iniziative, campagne e progetti dell'Associazione Agrigiochiamo: scuole, ricerca pedagogica,
            outdoor education e comunità che cresce.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 md:px-10 py-12 md:py-16">
        {/* Tabs */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex flex-wrap gap-1 rounded-full bg-white border-2 border-[#E5DCD0] p-1">
            {(["current", "upcoming", "past"]).map((s) => {
              const meta = STATUS_META[s];
              const Icon = meta.icon;
              const active = tab === s;
              return (
                <button
                  key={s}
                  onClick={() => setTab(s)}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
                    active ? "bg-[#4A6B53] text-white shadow-sm" : "text-[#58625B] hover:text-[#2C312E]"
                  }`}
                  data-testid={`projects-tab-${s}`}
                >
                  <Icon size={15}/> {meta.label}
                  <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-[#F2EBE1]"}`}>
                    {counts[s]}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#4A6B53]" size={28}/></div>
        ) : filtered.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-[#E5DCD0] p-14 text-center">
            <p className="font-display font-bold text-[#2C312E] mb-2">Nessun progetto in questa sezione</p>
            <p className="text-sm text-[#58625B]">Torna presto: nuovi progetti in arrivo.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => <ProjectCard key={p.id} project={p}/>)}
          </div>
        )}
      </section>
    </div>
  );
}

function ProjectCard({ project }) {
  const meta = STATUS_META[project.status] || STATUS_META.current;
  return (
    <Link
      to={`/progetti/${project.id}`}
      className="group flex flex-col overflow-hidden rounded-[2rem] bg-white border border-[#E5DCD0] shadow-sm transition-all hover:shadow-xl hover:border-[#C8795A]/40 hover:-translate-y-1"
      data-testid={`project-card-${project.id}`}
    >
      <div className="relative h-52 overflow-hidden bg-[#F2EBE1]">
        {project.cover_image && (
          <img
            src={imgSrc(project.cover_image)}
            alt={project.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C312E]/40 to-transparent"/>
        <span
          className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1 text-[11px] font-bold uppercase tracking-wider"
          style={{ color: meta.color }}
        >
          <span className="w-1 h-1 rounded-full" style={{ background: meta.color }}/>
          {meta.label}
        </span>
      </div>
      <div className="p-6 flex-1 flex flex-col gap-3">
        <h3 className="font-display font-black text-xl leading-snug text-[#2C312E]">{project.title}</h3>
        <p className="text-sm text-[#58625B] line-clamp-3">{project.excerpt}</p>
        {(project.start_date || project.end_date) && (
          <div className="flex items-center gap-1.5 text-xs text-[#7A7369] font-semibold mt-1">
            <Calendar size={12}/>
            {project.start_date}{project.end_date && ` — ${project.end_date}`}
          </div>
        )}
        <span className="inline-flex items-center gap-1 text-xs font-bold text-[#C8795A] mt-auto pt-2 group-hover:gap-2 transition-all">
          Leggi il progetto <ArrowRight size={14}/>
        </span>
      </div>
    </Link>
  );
}
